import { type Tournament } from '@clubspark-node/clubspark-tennis-types';
import { eventConstants, tools as factoryTools, genderConstants, mocksEngine } from 'tods-competition-factory';

import { DrawTypesEnum } from 'components/dialogs/draw/addDraw/utils';
import demoLevelConfigMock from 'mocks/demoTournaments/demoTournamentLevelConfig';
import { ABBREVIATED_ROUND_NAMING } from 'mocks/policies/ABBREVIATED_ROUND_NAMING';
import POLICY_PROGRESSION_DEFAULT from 'mocks/policies/PROGRESSION';
import POLICY_SCHEDULING_USTA from 'mocks/policies/SCHEDULING_USTA';
import { db } from 'services/storage/db';
import { isStandalone } from 'utils/environment';

const { MALE, FEMALE } = genderConstants;
const { TEAM, DOUBLES } = eventConstants;

const levelExtension = {
  name: 'level',
  value: {
    currentOrgLevelConfiguration: demoLevelConfigMock,
  },
};
const girls18s = 'U18 Girls Singles';
const rankings18s = 'RETRIEVAL.RANKING.SINGLES.U18';
const rankings16s = 'RETRIEVAL.RANKING.SINGLES.U16';
const startDate = factoryTools.dateTime.formatDate(new Date());
const regOpenStartDate = factoryTools.dateTime.addDays(startDate, 2);
const completedStartDate = factoryTools.dateTime.addDays(startDate, -7);
const clubCourts = {
  venueId: 'e8e4c0b0-216c-426f-bba2-18e16caa74b8', // ensure consistent venueId for courts shared across tournaments
  venueName: 'Club Courts',
  venueAbbreviation: 'CC',
  startTime: '08:00',
  endTime: '20:00',
  courtsCount: 6,
  courtNames: ['Court 1'],
  courtIds: ['a4e34807-c58e-4ab2-9a35-0fb46660baec'],
};
export const venueProfiles = [clubCourts];

const singlesWTN = 'RETRIEVAL.RATING.SINGLES.WTN';
const doublesWTN = 'RETRIEVAL.RATING.DOUBLES.WTN';
const singlesNTRP = 'RETRIEVAL.RATING.SINGLES.NTRP';
const qualifyingEventExtensions = [{ name: 'eventProfile', value: { qualifying: true } }];

const mockProfiles = [
  {
    tournamentAttributes: {
      timeItems: [
        { itemType: singlesWTN, itemValue: completedStartDate },
        { itemType: doublesWTN, itemValue: completedStartDate },
      ],
      tournamentId: 'E0B3E1F0-821E-48E0-A710-2223C6B49AA0',
    },
    tournamentName: 'Challenger',
    completeAllMatchUps: true,
    participantsProfile: { scaledParticipantsCount: 200 },
    drawProfiles: [
      {
        timeItems: [{ itemType: rankings18s, itemValue: completedStartDate }],
        category: { categoryName: 'U18' },
        eventName: 'U18 Boys Doubles',
        eventId: 'E0B3E1F0-event-1',
        drawId: 'E0B3E1F0-draw-1',
        eventType: DOUBLES,
        gender: MALE,
        seedsCount: 4,
        drawSize: 16,
      },
      {
        timeItems: [{ itemType: rankings16s, itemValue: completedStartDate }],
        drawType: DrawTypesEnum.FIRST_MATCH_LOSER_CONSOLATION,
        category: { categoryName: 'U16' },
        eventName: 'U16 Girls Singles',
        eventId: 'E0B3E1F0-event-2',
        drawId: 'E0B3E1F0-draw-2',
        gender: FEMALE,
        seedsCount: 8,
        drawSize: 32,
      },
      {
        category: { ratingType: 'WTN', ratingMin: 8, ratingMax: 12 },
        eventName: `WTN 8-12 SINGLES`,
        scaledParticipantsCount: 32,
        eventId: 'E0B3E1F0-event-3',
        drawId: 'E0B3E1F0-draw-3',
        seedsCount: 8,
        drawSize: 32,
      },
      {
        category: { ratingType: 'WTN', ratingMin: 8, ratingMax: 12 },
        eventName: `WTN 8-12 DOUBLES`,
        scaledParticipantsCount: 32,
        eventId: 'E0B3E1F0-event-4',
        drawId: 'E0B3E1F0-draw-4',
        eventType: DOUBLES,
        seedsCount: 8,
        drawSize: 32,
      },
    ],
    startDate: completedStartDate,
    venueProfiles,
  },
  {
    tournamentAttributes: { tournamentId: '13886A8A-E66D-42D9-BA79-FCEE0D2B9FB0' },
    tournamentName: 'Championships',
    drawProfiles: [
      {
        eventName: girls18s,
        uniqueParticipants: true,
        category: { categoryName: 'U18' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        drawType: DrawTypesEnum.CURTIS_CONSOLATION,
        eventId: '9e69ec35-event-1',
        drawId: '9e69ec35-draw-1',
        gender: FEMALE,
        drawSize: 64,
      },
      {
        uniqueParticipants: true,
        eventName: 'U18 Boys Singles',
        category: { categoryName: 'U18' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        drawType: DrawTypesEnum.FEED_IN_CHAMPIONSHIP,
        eventId: '9e69ec35-event-2',
        drawId: '9e69ec35-draw-2',
        gender: MALE,
        drawSize: 64,
      },
      {
        uniqueParticipants: true,
        eventName: 'U16 Girls Singles',
        category: { categoryName: 'U16' },
        timeItems: [{ itemType: rankings16s, itemValue: startDate }],
        eventId: '9e69ec35-event-3',
        drawId: '9e69ec35-draw-3',
        drawType: DrawTypesEnum.COMPASS,
        gender: FEMALE,
        drawSize: 64,
      },
      {
        uniqueParticipants: true,
        eventName: 'U16 Boys Singles',
        category: { categoryName: 'U16' },
        timeItems: [{ itemType: rankings16s, itemValue: startDate }],
        eventId: '9e69ec35-event-4',
        drawId: '9e69ec35-draw-4',
        drawType: DrawTypesEnum.COMPASS,
        gender: MALE,
        drawSize: 64,
      },
    ],
    startDate: regOpenStartDate,
    venueProfiles,
  },
  {
    tournamentAttributes: {
      timeItems: [
        { itemType: singlesWTN, itemValue: startDate },
        { itemType: doublesWTN, itemValue: startDate },
      ],
      tournamentId: 'A0B3E1F0-821E-48E0-A710-2223C6B49AA0',
    },
    tournamentName: 'WTN Level Based',
    participantsProfile: { scaledParticipantsCount: 200 },
    drawProfiles: [
      {
        category: { ratingType: 'WTN', ratingMin: 8, ratingMax: 12 },
        eventName: `WTN 8-12 SINGLES`,
        scaledParticipantsCount: 32,
        eventId: 'E0B3E1F0-event-3',
        drawId: 'E0B3E1F0-draw-3',
        seedsCount: 8,
        drawSize: 32,
      },
      {
        category: { ratingType: 'WTN', ratingMin: 8, ratingMax: 12 },
        eventName: `WTN 8-12 DOUBLES`,
        scaledParticipantsCount: 32,
        eventId: 'E0B3E1F0-event-4',
        drawId: 'E0B3E1F0-draw-4',
        eventType: DOUBLES,
        seedsCount: 8,
        drawSize: 32,
      },
      {
        eventName: `WTN 14-19 SINGLES`,
        category: { ratingType: 'WTN', ratingMin: 14, ratingMax: 19.99 },
        scaledParticipantsCount: 32,
        eventId: 'df952df3-event-1',
        drawId: 'df952df3-draw-1',
        drawType: DrawTypesEnum.COMPASS,
        seedsCount: 8,
        drawSize: 32,
      },
      {
        eventName: `WTN 20-26 SINGLES`,
        category: { ratingType: 'WTN', ratingMin: 20, ratingMax: 26 },
        scaledParticipantsCount: 16,
        eventId: 'df952df3-event-2',
        drawId: 'df952df3-draw-2',
        drawType: DrawTypesEnum.ROUND_ROBIN,
        seedsCount: 4,
        drawSize: 16,
      },
    ],
    venueProfiles,
    startDate,
  },
  {
    tournamentAttributes: {
      tournamentId: 'Z0B3E1F2-618B-27E3-B710-1113C6B49EE2',
      timeItems: [
        { itemType: singlesWTN, itemValue: completedStartDate },
        { itemType: doublesWTN, itemValue: completedStartDate },
      ],
    },
    tournamentName: 'NTRP Adult Open',
    participantsProfile: { scaledParticipantsCount: 200 },
    drawProfiles: [
      {
        eventName: `NTRP 3.0 Men's 18 & over singles`,
        eventId: 'ntrp-event-1',
        drawId: 'ntrp-draw-1',
        gender: MALE,
        drawSize: 16,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [3.0, 3.49] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 3.5 Men's 18 & over singles`,
        eventId: 'ntrp-event-2',
        drawId: 'ntrp-draw-2',
        gender: MALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [3.5, 3.99] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 4.0 Men's 18 & over singles`,
        eventId: 'ntrp-event-3',
        drawId: 'ntrp-draw-3',
        gender: MALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [4.0, 4.49] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 4.5 Men's 18 & over singles`,
        eventId: 'ntrp-event-4',
        drawId: 'ntrp-draw-4',
        gender: MALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [4.5, 4.99] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 5.0 Men's 18 & over singles`,
        eventId: 'ntrp-event-5',
        drawId: 'ntrp-draw-5',
        gender: MALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [5.0, 5.49] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        category: { categoryName: 'U18' },
        eventName: `Men's open singles`,
        eventId: 'ntrp-event-6',
        drawId: 'ntrp-draw-6',
        gender: MALE,
        drawSize: 8,
      },
      {
        eventName: `NTRP 2.5 Women's 18 & over singles`,
        eventId: 'ntrp-event-7',
        drawId: 'ntrp-draw-7',
        gender: FEMALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [2.5, 2.99] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 3.0 Women's 18 & over singles`,
        eventId: 'ntrp-event-8',
        drawId: 'ntrp-draw-8',
        gender: FEMALE,
        drawSize: 16,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [3.0, 3.49] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 3.5 Women's 18 & over singles`,
        eventId: 'ntrp-event-9',
        drawId: 'ntrp-draw-9',
        gender: FEMALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [3.5, 3.99] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 4.0 Women's 18 & over singles`,
        eventId: 'ntrp-event-10',
        drawId: 'ntrp-draw-10',
        gender: FEMALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [4.0, 4.49] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        eventName: `NTRP 4.5 Women's 18 & over singles`,
        eventId: 'ntrp-event-11',
        drawId: 'ntrp-draw-11',
        gender: FEMALE,
        drawSize: 8,
        category: {
          ratingAttributes: { decimalsCount: 2, step: 0.01, range: [4.5, 4.99] },
          ageCategoryCode: '18U',
          ratingType: 'NTRP',
        },
        eventAttributes: {
          timeItems: [
            {
              itemType: singlesNTRP,
              itemValue: startDate,
            },
          ],
        },
        eventExtensions: [
          {
            value: { ratingCategory: { value: 5 } },
            name: 'division',
          },
        ],
      },
      {
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        eventName: `Women's open singles`,
        category: { categoryName: 'U18' },
        eventId: 'ntrp-event-12',
        drawId: 'ntrp-draw-12',
        gender: FEMALE,
        drawSize: 8,
      },
    ],
    venueProfiles,
    startDate,
  },
  {
    tournamentAttributes: { tournamentId: 'aeec1a80-6454-4e31-8329-b0738ba53a84' },
    tournamentName: 'Dominant Duo',
    hydrateCollections: true,
    drawProfiles: [
      {
        category: { ageCategoryCode: 'U18' },
        tieFormatName: 'DOMINANT_DUO',
        eventId: 'aeec1a80-event-1',
        eventName: 'Duo Draw',
        eventType: TEAM,
        drawSize: 8,
      },
    ],
    venueProfiles,
    startDate,
  },
  {
    tournamentAttributes: { tournamentId: '89d9dc37-05fe-49bd-a0af-055af051e61f' },
    tournamentName: 'Dual Match',
    drawProfiles: [{ eventName: 'Team Event', eventType: TEAM, drawSize: 2, eventId: '89d9dc37-event-1' }],
    venueProfiles,
    startDate,
  },
  {
    tournamentAttributes: { tournamentId: '9e69ec35-d4f5-41f3-9c17-3268f461ea61' },
    tournamentName: 'Advanced Scheduling',
    drawProfiles: [
      {
        eventName: `Women's open singles`,
        category: { categoryName: '18U' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        drawType: DrawTypesEnum.CURTIS_CONSOLATION,
        eventId: '98989898-event-1',
        drawId: '98989898-draw-1',
        gender: FEMALE,
        drawSize: 32,
      },
      {
        eventName: `Men's open singles`,
        category: { categoryName: '18U' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        drawType: DrawTypesEnum.CURTIS_CONSOLATION,
        eventId: '98989898-event-2',
        drawId: '98989898-draw-2',
        gender: MALE,
        drawSize: 32,
      },
      {
        eventName: `Women's open doubles`,
        category: { categoryName: '18U' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        eventId: '98989898-event-3',
        drawId: '98989898-draw-3',
        eventType: DOUBLES,
        drawType: DrawTypesEnum.COMPASS,
        gender: FEMALE,
        drawSize: 32,
      },
      {
        eventName: `Men's open doubles`,
        category: { categoryName: '18U' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        eventId: '98989898-event-4',
        drawId: '98989898-draw-4',
        eventType: DOUBLES,
        drawType: DrawTypesEnum.COMPASS,
        gender: MALE,
        drawSize: 32,
      },
      {
        eventName: `Women's open doubles`,
        category: { categoryName: '18U' },
        timeItems: [{ itemType: rankings16s, itemValue: startDate }],
        eventId: '98989898-event-5',
        drawId: '98989898-draw-5',
        eventType: DOUBLES,
        drawType: DrawTypesEnum.COMPASS,
        gender: FEMALE,
        drawSize: 32,
      },
    ],
    venueProfiles: [
      clubCourts,
      {
        venueName: 'City Courts',
        venueAbbreviation: 'CTY',
        startTime: '08:00',
        endTime: '20:00',
        courtsCount: 12,
      },
      {
        venueName: 'Campus Courts',
        venueAbbreviation: 'CPS',
        startTime: '08:00',
        endTime: '20:00',
        courtsCount: 10,
      },
    ],
    startDate,
  },
  {
    tournamentAttributes: { tournamentId: '40cb3fb6-7ee6-4b31-9f0c-d08860807689' },
    tournamentName: 'Qualifying Structures',
    drawProfiles: [
      {
        drawSize: 16,
        eventId: '40cb3fb6-event-1',
        drawId: '40cb3fb6-draw-1',
        eventName: 'U18 Boys Singles',
        category: { categoryName: 'U18' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        eventExtensions: qualifyingEventExtensions,
        gender: MALE,
        seedsCount: 4,
        qualifyingProfiles: [
          {
            structureProfiles: [
              {
                qualifyingPositions: 4,
                drawSize: 16,
              },
            ],
          },
        ],
      },
      {
        drawSize: 16,
        eventId: '40cb3fb6-event-2',
        drawId: '40cb3fb6-draw-2',
        eventName: girls18s,
        category: { categoryName: 'U18' },
        timeItems: [{ itemType: rankings18s, itemValue: startDate }],
        eventExtensions: qualifyingEventExtensions,
        gender: FEMALE,
        seedsCount: 4,
        drawType: DrawTypesEnum.ROUND_ROBIN_WITH_PLAYOFF,
        qualifyingProfiles: [
          {
            structureProfiles: [
              {
                qualifyingPositions: 4,
                drawSize: 16,
              },
            ],
          },
        ],
      },
      {
        drawSize: 32,
        seedsCount: 8,
        completionGoal: 24,
        eventId: '40cb3fb6-event-3',
        drawId: '40cb3fb6-draw-3',
        eventName: 'WTN Progressive',
        category: { ratingType: 'WTN', ratingMin: 8, ratingMax: 12 },
        eventExtensions: qualifyingEventExtensions,
        scaledParticipantsCount: 60,
        qualifyingProfiles: [
          {
            roundTarget: 1,
            structureProfiles: [
              { stageSequence: 1, drawSize: 16, drawType: DrawTypesEnum.ROUND_ROBIN },
              { stageSequence: 2, drawSize: 16, qualifyingPositions: 4 },
            ],
          },
        ],
      },
    ],
    completeAllMatchUps: true,
    venueProfiles,
    startDate,
  },
];

const getItems = () => {
  const items: any[] = mockProfiles
    .map((mockProfile) => {
      const result = mocksEngine.generateTournamentRecord(mockProfile);

      return (
        result.tournamentRecord && {
          ...result.tournamentRecord,
          extensions: [
            ...(result.tournamentRecord?.extensions ?? []),
            {
              name: 'appliedPolicies',
              value: {
                ...ABBREVIATED_ROUND_NAMING,
                ...POLICY_PROGRESSION_DEFAULT,
                ...POLICY_SCHEDULING_USTA,
              },
            },
            levelExtension,
          ],
        }
      );
    })
    .filter(Boolean);

  const tournamentRecordString = localStorage.getItem('tournamentRecord');
  if (tournamentRecordString) {
    try {
      const tournamentRecord = JSON.parse(tournamentRecordString);
      items.push(tournamentRecord);
      if (isStandalone()) db.addTournament(tournamentRecord);
      localStorage.removeItem('tournamentRecord');
    } catch (err) {
      console.log('invalid tournamentRecord', { err });
    }
  }

  const mockProfile = localStorage.getItem('mockProfile');
  if (mockProfile) {
    const result = mocksEngine.generateTournamentRecord(JSON.parse(mockProfile));
    if (result.tournamentRecord) items.push(result.tournamentRecord);
  }

  return items;
};
export const demoTournamentsMock: { ['__typename']: string; items: Tournament[]; totalItems: number } = {
  __typename: 'PaginationResult_Tournament',
  items: getItems(),
  totalItems: 1,
};
